"use client";

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { siteUrl } from '@/lib/site-url';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithMagicLink: (email: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const navigate = (path: string) => router.push(path); // back-compat alias

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success('Welcome back!');
    navigate('/');
    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = siteUrl('/');

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success('Account created! Please check your email to verify.');
    return { error: null };
  };

  const signInWithGoogle = async () => {
    const redirectUrl = siteUrl('/');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    return { error: null };
  };

  const signInWithMagicLink = async (email: string) => {
    const redirectUrl = siteUrl('/');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success('Magic link sent! Check your email to sign in.');
    return { error: null };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = siteUrl('/reset-password');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success('Password reset link sent! Check your email.');
    return { error: null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success('Password updated successfully!');
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    toast.success('Signed out successfully');
    navigate('/auth');
  };

  return (
    <AuthContext.Provider value={{ user, session, signIn, signUp, signInWithGoogle, signInWithMagicLink, resetPassword, updatePassword, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// SSR-safe default. AuthProvider isn't mounted until after the first client
// render (deferred in app/providers.tsx to keep prerendering pure). Components
// can call useAuth() anywhere and get a sensible no-op shape during prerender —
// real state arrives on hydration.
const SSR_DEFAULT: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  signIn: async () => ({ error: new Error('Not mounted') }),
  signUp: async () => ({ error: new Error('Not mounted') }),
  signInWithGoogle: async () => ({ error: new Error('Not mounted') }),
  signInWithMagicLink: async () => ({ error: new Error('Not mounted') }),
  resetPassword: async () => ({ error: new Error('Not mounted') }),
  updatePassword: async () => ({ error: new Error('Not mounted') }),
  signOut: async () => undefined,
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  return context ?? SSR_DEFAULT;
};
